import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Autocomplete, Button, Chip, Grid, InputAdornment, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import FormDrawer from '../ui/FormDrawer';
import { FormSection, FormGrid } from '../ui/forms';
import type { MasterFormState } from './MasterFormDrawer';
import {
  getMasters,
  getTimezoneReference,
  getTimezoneUsage,
  type TimezoneReferenceItem,
} from '../../services/platform/masterService';
import {
  TIMEZONE_FIELD_LABELS,
  TIMEZONE_MAX_SORT_ORDER,
  currentOffset,
  localTime,
  nextTimezoneSortOrder,
  normalizeTimezoneName,
  validateTimezoneForm,
  type TimezoneExistingRecord,
} from '../../utils/timezoneMasterValidation';
import { limitConsecutiveRepeatedCharacters } from '../../utils/descriptionValidation';

interface Props {
  open: boolean;
  editing: boolean;
  form: MasterFormState;
  onChange: (form: MasterFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  editingId?: string;
  saveError?: string | null;
}

const str = (v: unknown) => (v == null ? '' : String(v).trim());
const lockAdornment = {
  readOnly: true,
  endAdornment: (
    <InputAdornment position="end">
      <Lock sx={{ fontSize: 14, color: 'text.disabled' }} />
    </InputAdornment>
  ),
};

function matches(o: TimezoneReferenceItem, q: string) {
  const query = q.trim().toLowerCase();
  if (!query) return true;
  const underscored = query.replace(/\s+/g, '_');
  const offset = query.replace(/^(utc|gmt)\s*/, '');
  return o.name.toLowerCase().includes(query)
    || o.ianaId.toLowerCase().includes(underscored)
    || o.abbreviation.toLowerCase() === query
    || o.countries.some((c) => c.toLowerCase() === query)
    || (!!offset && /^[+-]/.test(offset) && (o.utcOffset.startsWith(offset) || !!o.dstOffset?.startsWith(offset)));
}

export default function TimezoneFormDrawer({
  open,
  editing,
  form,
  onChange,
  onClose,
  onSubmit,
  loading,
  editingId,
  saveError,
}: Props) {
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const initializedFor = useRef<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const meta = form.metadata;
  const ianaId = str(meta.ianaId);

  const { data: reference, isLoading: referenceLoading } = useQuery({
    queryKey: ['timezone-reference'],
    queryFn: getTimezoneReference,
    enabled: open,
    staleTime: 60 * 60_000,
  });
  const { data: existingRaw, isFetching: existingLoading } = useQuery({
    queryKey: ['masters', 'timezones', 'form-siblings'],
    queryFn: () => getMasters('timezones', { limit: 2000, page: 1 }),
    enabled: open,
    staleTime: 0,
  });
  const existing = useMemo<TimezoneExistingRecord[]>(
    () => ((existingRaw?.data ?? []) as TimezoneExistingRecord[]),
    [existingRaw],
  );
  const { data: usage } = useQuery({
    queryKey: ['timezone-usage', editingId],
    queryFn: () => getTimezoneUsage(editingId!),
    enabled: open && editing && !!editingId,
    staleTime: 0,
  });

  const options = reference?.timezones ?? [];
  const selected = useMemo<TimezoneReferenceItem | null>(
    () => options.find((o) => o.ianaId.toLowerCase() === ianaId.toLowerCase()) || null,
    [options, ianaId],
  );
  const takenZones = useMemo(
    () => new Set(existing.filter((r) => r._id !== editingId).map((r) => str(r.metadata?.ianaId).toLowerCase())),
    [existing, editingId],
  );
  const suggestedSortOrder = useMemo(() => nextTimezoneSortOrder(existing, editingId), [existing, editingId]);

  useEffect(() => {
    if (!open) {
      initializedFor.current = null;
      return;
    }
    const key = editing ? `edit:${editingId}` : 'create';
    if (initializedFor.current === key) return;
    initializedFor.current = key;
    setTouched(new Set());
    if (!editing) {
      onChange({ ...form, code: '', name: '', description: '', isActive: true, sortOrder: 0, metadata: {} });
    }
  }, [open, editing, editingId, form, onChange]);

  useEffect(() => {
    if (!open || editing || initializedFor.current !== 'create' || existingLoading) return;
    if (!touched.has('sortOrder') && form.sortOrder !== suggestedSortOrder) {
      onChange({ ...form, sortOrder: suggestedSortOrder });
    }
  }, [open, editing, existingLoading, touched, suggestedSortOrder, form, onChange]);

  const errors = useMemo(
    () => validateTimezoneForm(
      { name: form.name, description: form.description, sortOrder: form.sortOrder, metadata: meta },
      { existing, editingId, knownZone: !!selected },
    ),
    [form.name, form.description, form.sortOrder, meta, existing, editingId, selected],
  );
  const blocking = Object.keys(errors).map((key) => [key, TIMEZONE_FIELD_LABELS[key] || key] as const);
  const isValid = blocking.length === 0 && !existingLoading && !referenceLoading;

  const touch = (...keys: string[]) => setTouched((prev) => {
    const next = new Set(prev);
    keys.forEach((k) => next.add(k));
    return next;
  });
  const errorFor = (key: string) => (touched.has(key) || /already/.test(errors[key] || '') ? errors[key] : undefined);
  const focusField = (key: string) => {
    touch(key);
    contentRef.current?.querySelector(`[data-field="${key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const applyZone = (option: TimezoneReferenceItem | null) => {
    if (!option) {
      onChange({ ...form, code: '', name: '', metadata: {} });
      return;
    }
    onChange({
      ...form,
      code: option.code,
      name: option.name,
      metadata: {
        ianaId: option.ianaId,
        utcOffset: option.utcOffset,
        dstSupported: option.dstSupported,
        abbreviation: option.abbreviation,
        ...(option.dstOffset ? { dstOffset: option.dstOffset } : {}),
      },
    });
    touch('ianaId', 'name');
  };

  const handleSubmit = () => {
    touch(...Object.keys(errors));
    if (!isValid) return;
    onSubmit();
  };

  const usedBy = usage && usage.total > 0
    ? [
      usage.tenantDefault ? 'the tenant default timezone' : '',
      usage.systemSetting ? 'the Default Timezone setting' : '',
      usage.countries ? `${usage.countries} countr${usage.countries === 1 ? 'y' : 'ies'}` : '',
      usage.zones ? `${usage.zones} operational zone${usage.zones === 1 ? '' : 's'}` : '',
      usage.workingCalendars ? `${usage.workingCalendars} working calendar${usage.workingCalendars === 1 ? '' : 's'}` : '',
    ].filter(Boolean).join(', ')
    : '';
  const actionLabel = editing ? 'Update' : 'Create';
  const nowOffset = selected ? currentOffset(selected.ianaId) : '';
  const dstNow = !!selected?.dstSupported && nowOffset !== selected.utcOffset;
  const code = selected?.code || str(form.code);

  return (
    <FormDrawer
      open={open}
      title={`${editing ? 'Edit' : 'Add'} Timezone`}
      subtitle="Pick an IANA timezone — code, UTC offset, daylight saving and abbreviation are derived from it."
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={actionLabel}
      loading={loading}
      disableSubmit={!isValid}
      submitHint={blocking.length
        ? `${blocking.length} item${blocking.length === 1 ? '' : 's'} to complete before you can ${actionLabel.toLowerCase()} — see the checklist at the top`
        : undefined}
      width={680}
      contentRef={contentRef}
    >
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
      {blocking.length > 0 && (
        <Alert severity={ianaId ? 'warning' : 'info'} sx={{ mb: 2 }} data-validation-summary>
          <Typography variant="body2" fontWeight={600} gutterBottom={!!ianaId}>
            {ianaId ? `Complete these to enable ${actionLabel}:` : 'Start by picking a timezone from the IANA list.'}
          </Typography>
          {ianaId && (
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {blocking.map(([key, label]) => (
                <Chip
                  key={key}
                  size="small"
                  label={`${label}: ${errors[key]}`}
                  color="warning"
                  variant="outlined"
                  onClick={() => focusField(key)}
                  sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
                />
              ))}
            </Stack>
          )}
        </Alert>
      )}
      {editing && (
        <Alert severity={usedBy ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {usedBy
            ? `Used by ${usedBy}. The IANA ID and code are locked, and it cannot be deactivated or deleted while in use.`
            : 'The IANA ID and code are locked after creation.'}
        </Alert>
      )}

      <FormSection title="Timezone" divider>
        <FormGrid>
          <Grid item xs={12} data-field="ianaId">
            {editing ? (
              <TextField
                fullWidth
                size="small"
                label="IANA timezone ID"
                value={ianaId}
                helperText="Locked"
                InputProps={{ ...lockAdornment, sx: { fontFamily: 'monospace' } }}
              />
            ) : (
              <Autocomplete<TimezoneReferenceItem>
                options={options}
                loading={referenceLoading || existingLoading}
                value={selected}
                onChange={(_, value) => applyZone(value)}
                onBlur={() => touch('ianaId')}
                groupBy={(o) => o.ianaId.split('/')[0]}
                getOptionLabel={(o) => `${o.ianaId} — ${o.name}`}
                isOptionEqualToValue={(o, v) => o.ianaId === v.ianaId}
                getOptionDisabled={(o) => takenZones.has(o.ianaId.toLowerCase())}
                filterOptions={(opts, s) => opts.filter((o) => matches(o, s.inputValue))}
                renderOption={(props, o) => (
                  <li {...props} key={o.ianaId}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                      <Typography variant="caption" fontFamily="monospace" sx={{ width: 82, flexShrink: 0 }}>
                        UTC{o.utcOffset}
                      </Typography>
                      <BoxText primary={o.ianaId} secondary={o.name} />
                      {o.abbreviation && <Typography variant="caption" color="text.secondary">{o.abbreviation}</Typography>}
                      {o.dstSupported && <Chip size="small" label="DST" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                      {takenZones.has(o.ianaId.toLowerCase()) && <Chip size="small" label="Already added" />}
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    size="small"
                    label="IANA timezone"
                    placeholder="Search city, Region/City, abbreviation (IST), country code (IN) or offset (+05:30)"
                    error={!!errorFor('ianaId')}
                    helperText={errorFor('ianaId')
                      || `${options.length ? `${options.length - takenZones.size} of ${options.length} timezones available` : 'Loading timezones…'} · format Region/City, e.g. Asia/Kolkata`}
                  />
                )}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={8} data-field="name">
            <TextField
              fullWidth
              size="small"
              required
              label="Timezone name"
              value={form.name}
              disabled={!ianaId}
              onChange={(e) => {
                onChange({ ...form, name: limitConsecutiveRepeatedCharacters(e.target.value).replace(/^\s+/, '') });
                touch('name');
              }}
              onBlur={() => {
                touch('name');
                const clean = normalizeTimezoneName(form.name);
                if (clean !== form.name) onChange({ ...form, name: clean });
              }}
              error={!!errorFor('name')}
              helperText={errorFor('name') || 'Display name · unique · 2–80 characters'}
              inputProps={{ maxLength: 80 }}
              InputProps={{
                endAdornment: selected && normalizeTimezoneName(form.name) !== selected.name ? (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => { onChange({ ...form, name: selected.name }); touch('name'); }}>
                      Use standard
                    </Button>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Code"
              value={code}
              helperText="Auto from IANA ID"
              InputProps={{ ...lockAdornment, sx: { fontFamily: 'monospace', fontSize: 13 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="sortOrder">
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label="Sort order"
              value={form.sortOrder >= 1 ? form.sortOrder : ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (/[-+,eE.]/.test(raw)) return;
                onChange({ ...form, sortOrder: raw === '' ? 0 : Number(raw) });
                touch('sortOrder');
              }}
              onBlur={() => touch('sortOrder')}
              error={!!errorFor('sortOrder')}
              helperText={errorFor('sortOrder') || `Unique · next available is ${suggestedSortOrder}`}
              inputProps={{ min: 1, max: TIMEZONE_MAX_SORT_ORDER, step: 1 }}
              InputProps={{
                endAdornment: form.sortOrder !== suggestedSortOrder && !editing ? (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => { onChange({ ...form, sortOrder: suggestedSortOrder }); touch('sortOrder'); }}>
                      Use {suggestedSortOrder}
                    </Button>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {editing
                ? 'Use the status toggle in the Timezones list to activate or deactivate. Only active timezones can be picked for countries.'
                : 'New timezones are created as Active and become selectable for countries in that zone.'}
            </Typography>
          </Grid>
          <Grid item xs={12} data-field="description">
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Description"
              value={form.description}
              onChange={(e) => {
                onChange({ ...form, description: limitConsecutiveRepeatedCharacters(e.target.value) });
                touch('description');
              }}
              onBlur={() => touch('description')}
              error={!!errorFor('description')}
              helperText={errorFor('description') || `${str(form.description).length}/500 · no repeated characters or words`}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
        </FormGrid>
      </FormSection>

      <FormSection title="Offset & daylight saving (from IANA)">
        <FormGrid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="UTC offset"
              value={selected ? `UTC${selected.utcOffset}` : ''}
              helperText="Standard time"
              InputProps={{ ...lockAdornment, sx: { fontFamily: 'monospace' } }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="DST supported"
              value={selected ? (selected.dstSupported ? 'Yes' : 'No') : ''}
              helperText="From IANA rules"
              InputProps={lockAdornment}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="DST offset"
              value={selected ? (selected.dstOffset ? `UTC${selected.dstOffset}` : '—') : ''}
              helperText="Summer time"
              InputProps={{ ...lockAdornment, sx: { fontFamily: 'monospace' } }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Abbreviation"
              value={selected ? selected.abbreviation || '—' : ''}
              helperText="From IANA data"
              InputProps={lockAdornment}
            />
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ px: 2, py: 1.25, bgcolor: 'action.hover' }}>
              {selected ? (
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    Now in {selected.ianaId.replace(/_/g, ' ')}: <strong>{localTime(selected.ianaId)}</strong>
                    {' · '}UTC{nowOffset}
                    {selected.dstSupported && (dstNow ? ' (daylight saving in effect)' : ' (standard time)')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selected.dstSupported
                      ? `Offsets switch automatically between UTC${selected.utcOffset} and UTC${selected.dstOffset} — schedules and reports always use the offset valid on that date.`
                      : `Fixed at UTC${selected.utcOffset} all year — this zone does not observe daylight saving time.`}
                    {selected.countries.length ? ` Used in: ${selected.countries.join(', ')}.` : ''}
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Offset, daylight saving and abbreviation fill in when you pick a timezone — they cannot be edited, so they always match the IANA definition.
                </Typography>
              )}
            </Paper>
          </Grid>
        </FormGrid>
      </FormSection>
    </FormDrawer>
  );
}

function BoxText({ primary, secondary }: { primary: string; secondary: string }) {
  return (
    <Stack sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" noWrap>{primary.replace(/_/g, ' ')}</Typography>
      <Typography variant="caption" color="text.secondary" noWrap>{secondary}</Typography>
    </Stack>
  );
}
